import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Lightweight loader so we don't depend on anything else
let mapsPromise;
function loadMapsInline() {
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (mapsPromise) return mapsPromise;

  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) return Promise.reject(new Error("Missing REACT_APP_GOOGLE_MAPS_API_KEY"));

  const url = new URL("https://maps.googleapis.com/maps/api/js");
  url.searchParams.set("key", key);
  url.searchParams.set("libraries", "places");
  url.searchParams.set("v", "quarterly");

  mapsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = url.toString();
    s.async = true;
    s.onerror = () => reject(new Error("Failed to load Google Maps JS"));
    s.onload = () => {
      if (window.google?.maps?.places) resolve(window.google);
      else reject(new Error("Google Maps loaded without Places"));
    };
    document.head.appendChild(s);
  });

  return mapsPromise;
}

export default function NearbyGymsPreview() {
  const { t } = useTranslation();

  const [gyms, setGyms] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const svcElRef = useRef(null); // invisible container for PlacesService

  async function fetchGyms() {
    try {
      setLoading(true);
      setErr("");

      const coords = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve(pos.coords),
          (e) => reject(e),
          { enableHighAccuracy: false, maximumAge: 60000, timeout: 12000 }
        );
      });

      const g = await loadMapsInline();

      if (!svcElRef.current) {
        svcElRef.current = document.createElement("div");
      }
      const svc = new g.maps.places.PlacesService(svcElRef.current);

      const center = new g.maps.LatLng(coords.latitude, coords.longitude);

      const results = await new Promise((resolve) => {
        svc.nearbySearch(
          {
            location: center,
            radius: 6000,
            type: "gym",
          },
          (res, status) => {
            if (status === g.maps.places.PlacesServiceStatus.OK || status === "OK") {
              resolve(res || []);
            } else {
              resolve([]);
            }
          }
        );
      });

      const dedup = new Map();
      for (const p of results || []) {
        if (p?.place_id && !dedup.has(p.place_id)) dedup.set(p.place_id, p);
      }

      const sorted = Array.from(dedup.values()).sort(
        (a, b) =>
          (b.rating || 0) - (a.rating || 0) ||
          String(a.name).localeCompare(String(b.name))
      );

      setGyms(
        sorted.slice(0, 5).map((p) => ({
          placeId: p.place_id,
          name: p.name,
          address: p.vicinity || p.formatted_address || "",
          rating: p.rating ?? "N/A",
        }))
      );
      if (!sorted.length) setErr(t("no_gyms_found"));
    } catch (e) {
      if (e?.message?.toLowerCase().includes("position")) {
        setErr(t("enable_location"));
      } else if (e?.message?.toLowerCase().includes("key")) {
        setErr(t("missing_api_key"));
      } else {
        setErr(t("unable_to_fetch_gyms"));
      }
      setGyms([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await fetchGyms();
    })();
    const onFocus = () => fetchGyms();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="home-card"
      style={{
        background: "#141414",
        border: "1px solid #0a3d4f",
        borderRadius: 12,
        padding: "14px 16px",
        minHeight: 180,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h4 style={{ margin: 0, color: "#00ffcc" }}>{t("gyms_near_you")}</h4>
        <Link to="/nearby-gyms" style={{ color: "#79e5ff", textDecoration: "none" }}>
          {t("more")} →
        </Link>
      </div>

      {loading ? (
        <div style={{ opacity: 0.7, marginTop: 8 }}>{t("finding_gyms")}</div>
      ) : err ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: "tomato", marginBottom: 8 }}>{err}</div>
          <button
            type="button"
            onClick={fetchGyms}
            style={{
              border: "1px solid #00bfff",
              background: "transparent",
              color: "#e8f6ff",
              borderRadius: 8,
              padding: "6px 10px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            {t("enable_location_retry")}
          </button>
        </div>
      ) : gyms.length ? (
        <ul style={{ listStyle: "none", margin: 10, padding: 0 }}>
          {gyms.map((g) => (
            <li
              key={g.placeId}
              style={{ padding: "8px 0", borderBottom: "1px solid #1f2a30" }}
              title={g.address}
            >
              <div style={{ fontWeight: 700 }}>{g.name}</div>
              <small style={{ opacity: 0.8, display: "block" }}>
                ⭐ {g.rating} · {g.address}
              </small>
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ opacity: 0.7, marginTop: 8 }}>{t("no_results")}</div>
      )}
    </div>
  );
}